import React, { useState, useEffect } from "react";
import { defineMessages, useIntl } from "react-intl";
import clsx from "clsx";
import { Button, IconButton, makeStyles, MobileStepper } from "@material-ui/core";
import { KeyboardArrowLeft, KeyboardArrowRight, ClearIcon } from "@material-ui/icons";
import CloseMenuIcon from "../icons/closeMenuIcon";
import { handleDownload, isImage } from "./attachmentsHelper";
import { FormattedMessage } from "react-intl";

const useStyles = makeStyles((theme) => ({
    lightbox: {
        zIndex: 3000,
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "rgba(0, 0, 0, 0.3)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexGrow: 1,
    },
    lightboxContent: {
        margin: "auto",
    },
    lightboxContentMobile: {
        display: "flex",
        flexFlow: "column",
        height: "100%",
        width: "100%",
    },
    lightboxImageContainer: {
        backgroundColor: "var(--text-dark)",
        padding: "8px",
        paddingBottom: "48px",
    },
    containerDesktop: {
        height: "50vh",
        width: "60vw",
    },
    containerMobile: {
        flex: "1 1 auto",
        width: "100%",
    },
    lightBoxHeader: {
        display: "flex",
        alignItems: "center",
        height: 50,
        backgroundColor: "var(--text-dark)",
        padding: "16px 0px 16px 16px",
    },
    lightBoxHeaderMobile: {
        flex: "0 1 auto",
    },
    lightBoxFooterMobile: {
        flex: "0 1 auto",
    },
    button: {
        color: "white",
        textAlign: "center",
        paddingTop: ".8vh",
    },
    fileName: {
        color: "white",
        fontFamily: "BellSlim",
        fontWeight: "bold",
        fontSize: "18px",
        width: "100%",
    },
    image: {
        objectFit: "cover",
        margin: "auto",
        display: "block",
        maxHeight: "100%",
        maxWidth: "100%",
    },
    preview: {
        color: "white",
        textAlign: "center",
        paddingTop: "18vh",
    },
    previewMobile: {
        color: "white",
        textAlign: "center",
        paddingTop: "5vh",
    },
    previewText: {
        padding: "5px",
    },
}));

export const Lightbox = (props) => {
    const intl = useIntl();

    const [fileToShow, setFileToShow] = useState(props.files[props.fileIndex]);
    const [activeStep, setActiveStep] = useState(props.fileIndex);

    const classes = useStyles();

    useEffect(() => {
        setFileToShow(props.files[props.fileIndex]);
        setActiveStep(props.fileIndex);
    }, [props.fileIndex]);

    const showNext = (e) => {
        e.stopPropagation();
        let currentIndex = props.files.indexOf(fileToShow);
        let nextfileIndex = currentIndex >= props.files.length - 1 ? 0 : currentIndex + 1;
        setFileToShow(props.files[nextfileIndex]);
        setActiveStep(nextfileIndex);
    };

    const showPrevious = (e) => {
        e.stopPropagation();
        let currentIndex = props.files.indexOf(fileToShow);
        let nextfileIndex = currentIndex <= 0 ? props.files.length - 1 : currentIndex - 1;
        setFileToShow(props.files[nextfileIndex]);
        setActiveStep(nextfileIndex);
    };

    // When uploading was originally implemented there was an issue with file type being set in the service.
    // So to make sure old files that were uploaded before today (March 24 2021) can be downloaded,
    // We're temporarily adding this function to format it correctly.
    // (This should be removed eventually)
    const imageFileType = (fileType) => {
        return fileType.includes("image") ? fileType : `image/${fileType}`;
    };

    return (
        <>
            <div>
                {props.open && fileToShow && (
                    <div className={classes.lightbox}>
                        <div
                            className={clsx({
                                [classes.lightboxContent]: !props.mobile,
                                [classes.lightboxContentMobile]: props.mobile,
                            })}>
                            <div className={clsx(classes.lightBoxHeader, { [classes.lightBoxHeaderMobile]: props.mobile })}>
                                <div className={classes.fileName}>
                                    <FormattedMessage
                                        id="common-lightbox.header"
                                        defaultMessage="{name} ({size} bytes)"
                                        description="Lightbox header, file name with size in bytes"
                                        values={{
                                            name: fileToShow.fileName ? fileToShow.fileName : fileToShow.fullFileName,
                                            size: fileToShow.size ? fileToShow.size : fileToShow.data.length,
                                        }}
                                    />
                                </div>
                                <IconButton onClick={() => props.onClose()}>
                                    <CloseMenuIcon color="white" />
                                </IconButton>
                            </div>
                            <div
                                className={clsx(classes.lightboxImageContainer, {
                                    [classes.containerDesktop]: !props.mobile,
                                    [classes.containerMobile]: props.mobile,
                                })}>
                                {isImage(fileToShow.fileType) ? (
                                    <img
                                        className={classes.image}
                                        alt={fileToShow.fileName}
                                        src={`data:${imageFileType(fileToShow.fileType)};base64,${fileToShow.data}`}
                                    />
                                ) : (
                                    <div
                                        className={clsx({
                                            [classes.preview]: !props.mobile,
                                            [classes.previewMobile]: props.mobile,
                                        })}>
                                        <div className={classes.previewText}>
                                            <FormattedMessage
                                                id="common-lightbox.no-preview"
                                                defaultMessage="No preview available"
                                                description="Lightbox no preview message"
                                            />
                                        </div>
                                    </div>
                                )}
                                <div className={classes.button}>
                                    <Button variant="contained" color="primary" onClick={() => handleDownload(fileToShow)}>
                                        <FormattedMessage
                                            id="common-lightbox.download"
                                            defaultMessage="Download"
                                            description="Lightbox download button"
                                        />
                                    </Button>
                                </div>
                            </div>
                            <div className={clsx({ [classes.lightBoxFooterMobile]: props.mobile })}>
                                <MobileStepper
                                    variant="dots"
                                    steps={props.files.length}
                                    position="static"
                                    activeStep={activeStep}
                                    className={classes.root}
                                    nextButton={
                                        <IconButton color="primary" onClick={showNext}>
                                            <KeyboardArrowRight />
                                        </IconButton>
                                    }
                                    backButton={
                                        <IconButton color="primary" onClick={showPrevious}>
                                            <KeyboardArrowLeft />
                                        </IconButton>
                                    }
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};
